import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LoyaltyIcon from "@mui/icons-material/Loyalty";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useCompany } from "../../components/context/CompanyContext";
import IndicadoresNav from "../../components/IndicadoresNav";
import Loading from "../../components/loading/loading";
import CustomGrid from "../../components/grid/CustomGrid";
import { titleSetRef } from "../../redux/titleRedux";
import { userRequest } from "../../requestMethods";
import { formatPricing } from "../../util/helper";
import { effectiveStories } from "../../util/storeScope";
import "../../App.css";

const BRAND = "#EE8253";

const SEGMENT_COLORS = {
  ativo:      { bg: "#c7f6da", color: "#06632b", border: "#06632b33" },
  preInativo: { bg: "#FEF9C3", color: "#854D0E", border: "#854D0E33" },
  inativo:    { bg: "#f1d1c3", color: "#863c1c", border: "#863c1c33" },
  critico:    { bg: "rgb(243,142,142)", color: "#c00", border: "#c0000033" },
};

const GROUP_COLORS = {
  0: { from: "#6B7280", to: "#374151" },
  1: { from: "#3B82F6", to: "#1D4ED8" },
  2: { from: "#EE8253", to: "#E8502A" },
};

function SectionLabel({ color, icon, text, totalCount, totalValue }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 4, height: 22, borderRadius: 2, background: color, flexShrink: 0 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 6, color }}>
          {icon}
          <span style={{ fontSize: 13, fontWeight: 800, fontFamily: "Manrope", letterSpacing: "0.01em" }}>{text}</span>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        <span style={{ fontSize: 12, color: "#6B7280", fontFamily: "Manrope" }}>{totalCount} cliente{totalCount !== 1 ? "s" : ""}</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "Manrope" }}>{formatPricing(totalValue)}</span>
      </div>
    </div>
  );
}

const SEGMENT_ICONS = {
  ativo:      <CheckCircleOutlineIcon  style={{ fontSize: 18 }} />,
  preInativo: <HourglassEmptyIcon      style={{ fontSize: 18 }} />,
  inativo:    <PauseCircleOutlineIcon  style={{ fontSize: 18 }} />,
  critico:    <CancelOutlinedIcon      style={{ fontSize: 18 }} />,
};

function SegmentCard({ label, sub, count, value, active, onClick }) {
  const [hover, setHover] = useState(false);
  const cfg = SEGMENT_COLORS[active] || { bg: "#F3F4F6", color: "#374151", border: "#37415133" };
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: cfg.bg,
        border: `1.5px solid ${cfg.border}`,
        borderRadius: 12,
        padding: "14px 16px",
        cursor: "pointer",
        transition: "transform 0.15s, box-shadow 0.15s",
        transform: hover ? "translateY(-2px)" : "none",
        boxShadow: hover ? "0 4px 12px rgba(0,0,0,0.12)" : "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      {/* Ícone + label */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: `${cfg.color}18`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: cfg.color, flexShrink: 0,
        }}>
          {SEGMENT_ICONS[active]}
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 800, color: cfg.color, fontFamily: "Manrope", lineHeight: 1.2 }}>{label}</div>
          <div style={{ fontSize: 10, color: cfg.color, opacity: 0.65, fontFamily: "Manrope" }}>{sub}</div>
        </div>
      </div>
      {/* Valor */}
      <div style={{ fontSize: 20, fontWeight: 900, color: cfg.color, fontFamily: "Manrope", letterSpacing: "-0.01em" }}>{formatPricing(value)}</div>
      <div style={{ fontSize: 11, color: cfg.color, opacity: 0.65, fontFamily: "Manrope", marginTop: 3 }}>{count} cliente{count !== 1 ? "s" : ""}</div>
    </div>
  );
}

function GroupSection({ children }) {
  return (
    <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", boxShadow: "0 2px 6px rgba(0,0,0,0.05)", padding: "14px" }}>
      {children}
    </div>
  );
}

const GRID_COLUMNS = [
  { field: "nome_cliente",             type: "text",   headerName: "Cliente",          width: 160, align: "left"   },
  { field: "cnpj_cpf",                 type: "text",   headerName: "CNPJ/CPF",         width: 120, align: "center" },
  { field: "contact_cliente",          type: "text",   headerName: "Tel",              width: 110, align: "center" },
  { field: "grupo_compra",             type: "text",   headerName: "Grupo",            width:  90, align: "center" },
  { field: "categoria",                type: "text",   headerName: "Categoria",        width: 100, align: "center" },
  { field: "total_compras",            type: "number", headerName: "Compras",          width:  80, align: "center" },
  { field: "valor_ultima_compra",      type: "number", headerName: "Últ. Compra R$",   width: 120, align: "right",  format: "BRL" },
  { field: "ultima_compra",            type: "date",   headerName: "Data Últ.",        width: 100, align: "center", format: "dd/MM/yyyy" },
  { field: "dias_desde_ultima_compra", type: "number", headerName: "Dias",             width:  70, align: "center" },
  { field: "vendedor",                 type: "text",   headerName: "Vendedor",         width: 120, align: "center" },
];

export default function IndicadoresEstrategica() {
  const [salesGroup, setSalesGroup] = useState([]);
  const [gridRows, setGridRows] = useState(null);
  const [gridTitle, setGridTitle] = useState("");
  const [stories, setStories] = useState([]);
  const [selected, setSelected] = useState([]);
  const [storeIds, setStoreIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const currentUser = useSelector((state) => state.user.currentUser);
  const { companyId } = useCompany();
  const nav = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(titleSetRef({ title: "Vend. Estratégica" }));
    if (!currentUser) nav("../login");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (companyId > 0 && currentUser) getStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    setStoreIds(selected.map((item) => item.value));
  }, [selected]);

  useEffect(() => {
    if (companyId > 0 && currentUser) {
      setIsLoading(true);
      getAllOrdersSales();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, storeIds]);

  const getStories = async () => {
    try {
      const res = await userRequest.get(`${companyId}/store/stories`);
      const list = res.data.map((item) => ({ label: item.store, value: item.storeId }));
      setStories(list);
      if (currentUser?.position === "Gerente") {
        const userStore = list.find((s) => s.value === currentUser.storeId);
        if (userStore) setSelected([userStore]);
      }
    } catch {}
  };

  const getAllOrdersSales = async () => {
    try {
      if (!currentUser?.isAdmin) { setIsLoading(false); return; }
      let url = `${companyId}/reportOrderSales`;
      const eff = effectiveStories(currentUser, storeIds);
      if (eff.length > 0) url += `?storeIds=${eff.join(",")}`;
      const res = await userRequest.get(url);
      setSalesGroup(Array.isArray(res.data) ? res.data : []);
    } catch {
      setSalesGroup([]);
    } finally {
      setIsLoading(false);
    }
  };

  const grupo0 = salesGroup.filter((r) => r.total_compras === 0);
  const grupo1 = salesGroup.filter((r) => r.total_compras === 1);
  const grupo2 = salesGroup.filter((r) => r.total_compras >= 2);

  const somaValor = (lista) =>
    lista.reduce((acc, r) => acc + (Number(r.valor_ultima_compra) || 0), 0);

  const porDias = (lista) => ({
    ativo:      lista.filter((r) => r.dias_desde_ultima_compra <= 120),
    preInativo: lista.filter((r) => r.dias_desde_ultima_compra > 120 && r.dias_desde_ultima_compra <= 180),
    inativo:    lista.filter((r) => r.dias_desde_ultima_compra > 180 && r.dias_desde_ultima_compra <= 365),
    critico:    lista.filter((r) => r.dias_desde_ultima_compra > 365),
  });

  const g1d = porDias(grupo1);
  const g2d = porDias(grupo2);

  const openGrid = (rows, title) => {
    setGridRows(rows);
    setGridTitle(title);
    setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 50);
  };

  return (
    <div style={{ padding: "16px 16px 48px 4px", fontFamily: "Manrope", background: "#F9FAFB", minHeight: "100vh" }}>

      <IndicadoresNav />

      {/* Header + filtro loja */}
      <div id="est-header" style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, justifyContent: "space-between" }}>
        <div id="est-title">
          <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>Vendas Estratégica</span>
          <span style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 8 }}>segmentação de clientes</span>
        </div>

        {/* Pills de loja */}
        {stories.length > 0 && (() => {
          const isGerente = currentUser?.position === "Gerente";
          return (
            <div id="est-filter-lojas">
              <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Lojas</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
                <label
                  style={{ cursor: isGerente ? "default" : "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${selected.length === 0 ? "#EE8253" : "#E5E7EB"}`, background: selected.length === 0 ? "#EE8253" : "transparent", color: selected.length === 0 ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600 }}
                  onClick={() => !isGerente && setSelected([])}
                >
                  Todas
                </label>
                {stories.map((story) => {
                  const isActive = selected.some((s) => s.value === story.value);
                  return (
                    <label
                      key={story.value}
                      style={{ cursor: isGerente ? "default" : "pointer", padding: "3px 10px", borderRadius: 6, border: `1px solid ${isActive ? "#EE8253" : "#E5E7EB"}`, background: isActive ? "#EE8253" : "transparent", color: isActive ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600, opacity: isGerente ? 0.7 : 1 }}
                      onClick={() => {
                        if (isGerente) return;
                        setSelected(isActive ? selected.filter((s) => s.value !== story.value) : [...selected, story]);
                      }}
                    >
                      {story.label}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      {isLoading ? (
        <Loading />
      ) : !currentUser?.isAdmin ? (
        <div id="est-no-admin" style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", padding: "40px", textAlign: "center", color: "#9CA3AF", fontFamily: "Manrope" }}>
          Disponível apenas para administradores.
        </div>
      ) : (
        <div id="est-content" style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          {/* Grupo 0 — Nunca comprou */}
          <div id="est-grupo0" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <SectionLabel color={GROUP_COLORS[0].from} icon={<RemoveShoppingCartIcon style={{ fontSize: 16 }} />} text="0 Compras — Nunca comprou" totalCount={grupo0.length} totalValue={somaValor(grupo0)} />
            <GroupSection>
              <div id="est-grupo0-cards" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                <SegmentCard
                  label="Nunca comprou"
                  sub="Sem nenhum pedido finalizado"
                  count={grupo0.length}
                  value={somaValor(grupo0)}
                  active="inativo"
                  onClick={() => openGrid(grupo0, "0 Compras — Nunca comprou")}
                />
              </div>
            </GroupSection>
          </div>

          {/* Grupo 1 — 1 Compra */}
          <div id="est-grupo1" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <SectionLabel color={GROUP_COLORS[1].from} icon={<ShoppingCartIcon style={{ fontSize: 16 }} />} text="1 Compra" totalCount={grupo1.length} totalValue={somaValor(grupo1)} />
            <GroupSection>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                <SegmentCard label="Ativo"           sub="≤ 120 dias"   count={g1d.ativo.length}      value={somaValor(g1d.ativo)}      active="ativo"      onClick={() => openGrid(g1d.ativo,      "1 Compra — Ativo"           )} />
                <SegmentCard label="Pré-Inativo"     sub="121–180 dias" count={g1d.preInativo.length} value={somaValor(g1d.preInativo)} active="preInativo" onClick={() => openGrid(g1d.preInativo, "1 Compra — Pré-Inativo"     )} />
                <SegmentCard label="Inativo"         sub="181–365 dias" count={g1d.inativo.length}    value={somaValor(g1d.inativo)}    active="inativo"    onClick={() => openGrid(g1d.inativo,    "1 Compra — Inativo"         )} />
                <SegmentCard label="Inativo Crítico" sub="> 365 dias"   count={g1d.critico.length}    value={somaValor(g1d.critico)}    active="critico"    onClick={() => openGrid(g1d.critico,   "1 Compra — Inativo Crítico" )} />
              </div>
            </GroupSection>
          </div>

          {/* Grupo 2+ — 2 ou mais Compras */}
          <div id="est-grupo2" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <SectionLabel color={GROUP_COLORS[2].from} icon={<LoyaltyIcon style={{ fontSize: 16 }} />} text="2+ Compras — Clientes recorrentes" totalCount={grupo2.length} totalValue={somaValor(grupo2)} />
            <GroupSection>
              <div id="est-grupo2-cards" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
                <SegmentCard label="Ativo"           sub="≤ 120 dias"   count={g2d.ativo.length}      value={somaValor(g2d.ativo)}      active="ativo"      onClick={() => openGrid(g2d.ativo,      "2+ Compras — Ativo"           )} />
                <SegmentCard label="Pré-Inativo"     sub="121–180 dias" count={g2d.preInativo.length} value={somaValor(g2d.preInativo)} active="preInativo" onClick={() => openGrid(g2d.preInativo, "2+ Compras — Pré-Inativo"     )} />
                <SegmentCard label="Inativo"         sub="181–365 dias" count={g2d.inativo.length}    value={somaValor(g2d.inativo)}    active="inativo"    onClick={() => openGrid(g2d.inativo,    "2+ Compras — Inativo"         )} />
                <SegmentCard label="Inativo Crítico" sub="> 365 dias"   count={g2d.critico.length}    value={somaValor(g2d.critico)}    active="critico"    onClick={() => openGrid(g2d.critico,   "2+ Compras — Inativo Crítico" )} />
              </div>
            </GroupSection>
          </div>

          {/* Grid analítico — aparece ao clicar em um segmento */}
          {gridRows !== null && (
            <div id="est-grid-analitico" style={{ background: "#fff", borderRadius: 14, border: `1.5px solid ${BRAND}33`, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <div id="est-grid-header" style={{ background: `linear-gradient(135deg, ${BRAND}, #E8502A)`, padding: "14px 22px", borderRadius: "14px 14px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 14, fontWeight: 800, color: "#fff", fontFamily: "Manrope" }}>{gridTitle}</span>
                <span
                  onClick={() => setGridRows(null)}
                  style={{ fontSize: 12, color: "rgba(255,255,255,0.8)", cursor: "pointer", fontFamily: "Manrope", fontWeight: 600 }}
                >
                  Fechar ✕
                </span>
              </div>
              <div id="est-grid-body" style={{ padding: "16px", height: 480 }}>
                <CustomGrid
                  gridId="estrategicaDetailGrid"
                  rows={gridRows}
                  columns={GRID_COLUMNS}
                  pageSize={25}
                  checkboxSelection={false}
                />
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
